import React, { useState } from 'react';
import { MessageSquare, BookOpen, Download, Save, RefreshCw, Loader2 } from 'lucide-react';
import { useStore } from '../lib/store';
import { formatTeachingContent, downloadAsFile } from '../lib/exportUtils';
import { regenerateTeaching } from '../lib/teachingService';
import ReactMarkdown from 'react-markdown';
import { createGoogleDoc } from '../lib/googleDrive';

export const TeachingAnalysis = () => {
  const { teachings, hasValidApiKeys } = useStore();
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  if (teachings.length === 0) {
    return null;
  }

  const latestTeaching = teachings[teachings.length - 1];

  const handleExport = async (type: 'txt' | 'md' | 'html' | 'gdoc') => {
    setIsExporting(true);
    try {
      const content = formatTeachingContent(latestTeaching);
      const timestamp = latestTeaching.timestamp instanceof Date 
        ? latestTeaching.timestamp.toISOString().split('T')[0]
        : new Date(latestTeaching.timestamp).toISOString().split('T')[0];
      
      if (type === 'gdoc') {
        await createGoogleDoc(`Teaching - ${timestamp}`, content);
      } else {
        downloadAsFile(content, `teaching-${timestamp}`, type);
      }
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setIsExporting(false);
      setShowExportOptions(false);
    }
  };

  const handleRegenerate = async () => {
    if (!hasValidApiKeys()) {
      setError('Please configure your API keys first');
      return;
    }

    setIsRegenerating(true);
    setError(null);
    try {
      await regenerateTeaching(latestTeaching);
    } catch (error: any) {
      setError(error.message || 'Error regenerating teaching');
      console.error('Error regenerating teaching:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <div className="mt-8 w-full max-w-4xl mx-auto p-6 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-amber-500" />
          <h2 className="text-2xl font-bold text-amber-500">Teaching Content</h2>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isRegenerating ? 'animate-spin' : ''}`} />
            {isRegenerating ? 'Regenerating...' : 'Regenerate Teaching'}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              disabled={isExporting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors disabled:opacity-50 text-gray-900"
            >
              {isExporting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              Export
            </button>
            {showExportOptions && (
              <div className="absolute right-0 mt-2 w-48 rounded-lg bg-gray-800 shadow-lg py-2 z-10">
                <button
                  onClick={() => handleExport('txt')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-700 transition-colors"
                >
                  Text File (.txt)
                </button>
                <button
                  onClick={() => handleExport('md')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-700 transition-colors"
                >
                  Markdown (.md)
                </button>
                <button
                  onClick={() => handleExport('html')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-700 transition-colors"
                >
                  HTML Document (.html)
                </button>
                <button
                  onClick={() => handleExport('gdoc')}
                  className="w-full px-4 py-2 text-left hover:bg-gray-700 transition-colors"
                >
                  Google Doc
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div className="p-4 rounded-lg bg-gray-950">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Original Transcript</h3>
          <p className="text-gray-400">{latestTeaching.content}</p>
        </div>

        {latestTeaching.additionalTranscripts?.map((transcript, index) => (
          <div key={index} className="p-4 rounded-lg bg-gray-950">
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              Additional Transcript {index + 1}
            </h3>
            <p className="text-gray-400">{transcript.content}</p>
          </div>
        ))}

        <div className="p-4 rounded-lg bg-gray-950">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Formatted Teaching</h3>
          {isRegenerating ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
            </div>
          ) : (
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown>{latestTeaching.summary}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};